import React, { useCallback, useMemo, useRef } from 'react';
import { Layout } from 'tea-component';
import { isEqual } from 'lodash-es';
import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import { GAME_CATEGORY } from '@opm/components/FilterCommon/constants/games';
import MarketContentBody from '@opm/components/MarketContentBody';
import useMenuTitle from '@opm/hooks/useMenuTitle';
import useIntls from '@opm/hooks/useIntls';
import { DBO_TM } from '@op/utils/format';
import { getLanguage } from '@home/utils/common';
import { useHistory, useLocation } from 'umi';

import {
  ANCHORS,
  A_OVERVIEW,
  A_TREND,
  A_CHANNEL,
  A_COUNTRY,
} from './constants/anchors';
import { useAnchorScroll } from './hooks/useAnchorScroll';
import { GameColorProvider } from './context/GameColorContext';
import StickyNavigator from './components/StickyNavigator';
import KpiOverview, { KPI_OVERVIEW_TITLE } from './components/KpiOverview';
import TrendCompare, {
  TREND_COMPARISON_TITLE,
} from './components/TrendCompare';
import ChannelCompare, {
  CHANNEL_COMPARISON_TITLE,
} from './components/ChannelCompare';
import CountryCompare, {
  COUNTRY_COMPARISON_TITLE,
} from './components/CountryCompare';
import { TGameCategory, IFilter } from './types/filter';
import { IAnchorMeta } from './types/common';
import './index.scss';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { CompetitorAdComparisonPoint } from '@opm/operationLog/pages';
import {
  mergeMarketingBuriedPointForStudio,
  type IStudioEmbedLogConfig,
} from '@opm/utils/studioEmbedLog';
import { FilterCondGameIdsProvider } from '@opm/components/FilterCommon/context/FilterCondGameIdsContext';
import { adCreativesStopTipsMap } from '@opm/components/BubbleCommon';
import marketConnect from '@opm/components/MarketConnect';
import { dealwithCompetitorAdComparison } from '@opm/components/MarketConnect/utils/gamingAdIntelligence';
import { COMPETITOR_AD_LIST_API, TByGameGenre } from '@opm/components/MarketConnect/utils/materialsList';
import { useGenreDateAutoTime } from '@opm/hooks/useGenreDateAutoTime';
const { Content } = Layout; 

export interface ICompetitorAdComparisonProps {
  titleEn?: string;
  titleCn?: string;
  enableSourcePageLog?: boolean;
  hidePageHeader?: boolean;
  /** 隐藏游戏分类 Segment，固定使用「全部游戏」 */
  hideGameCategoryTabs?: boolean;
  defaultFilter?: Partial<IFilter>;
  /** 筛选器实例 ID，用于隔离 sessionStorage / Redux 筛选状态 */
  filterInstanceId?: string;
  /** Studio 嵌入时的 Tab logConfig，用于覆盖 FilterSubmit 等按钮打点 */
  studioLogConfig?: IStudioEmbedLogConfig;
  /** 传入 /api/v1/marketing/filter/cond 的 game_ids */
  condGameIds?: string[];
  /** 品类 → 最新数据日期映射（来自 API config.by_game_genre，日期格式 YYYY-MM-DD） */
  byGameGenre?: TByGameGenre;
}

/** 页面默认品类 */
const DEFAULT_GAME_CATEGORY: TGameCategory = 'Arrows-like';

/** 默认筛选：T-2，粒度 Day，品类 Arrows-like */
const baseDefaultFilter = {
  ...DBO_TM.filterRangeDay(2, 2),
  date_type: 'daily',
  game_category: DEFAULT_GAME_CATEGORY,
};

/**
 * 多游戏投放对比页 · 页面入口
 *
 * 需求文档：src/pages/marketing/specs/多游戏投放对比页/多游戏投放对比页.md
 * 技术方案：src/pages/marketing/specs/多游戏投放对比页/spec/plan.md
 *
 * T07 产出：FilterCommon 集成 + 四大模块结构骨架 + 吸顶联动
 */
function CompetitorAdComparison(props: ICompetitorAdComparisonProps = {}) {
  const {
    titleEn,
    titleCn,
    enableSourcePageLog = true,
    hidePageHeader = false,
    hideGameCategoryTabs = false,
    defaultFilter: defaultFilterProp,
    filterInstanceId,
    studioLogConfig,
    condGameIds,
    byGameGenre,
  } = props;
  const { currentTitle } = useMenuTitle();
  const { isEn } = useIntls();
  const location = useLocation();
  const stateParams = (location.state || {}) as Record<string, any>;
  const userDefaultFilter = useMemo(
    () => ({
      ...baseDefaultFilter,
      ...(hideGameCategoryTabs ? { game_category: GAME_CATEGORY.ALL } : {}),
      ...defaultFilterProp,
      ...(hideGameCategoryTabs ? { game_category: GAME_CATEGORY.ALL } : {}),
      ...stateParams,
    }),
    [defaultFilterProp, hideGameCategoryTabs, stateParams],
  );

  const buriedPoint = useMemo(
    () =>
      mergeMarketingBuriedPointForStudio(
        CompetitorAdComparisonPoint,
        studioLogConfig,
      ),
    [studioLogConfig],
  );

  DBOM_POINT.useInitPage({
    buriedPoint,
    triggerPagePoint: enableSourcePageLog,
  });
  // FilterCommon 集成（D16，参考 CompetitorsAdCreatives/index.tsx）
  // ⚠️ 多游戏对比专用：
  // - ADCompareGame：游戏选择器，最多 15 个，category 默认尊重外部传值
  // - ADMultiDateRange：日期范围（支持 Day/Week/Month 切换）
  const {
    filter: rawFilter,
    setFilter,
    loading,
    filterRef,
    ...filterProps
  } = useFilterCommon({
    keys: ['ADCompareGame', 'ADCompareMultiDateRange'],
    instanceId: filterInstanceId,
  });

  const handleConfirm = useCallback(
    (value: any) => {
      const normalizedValue = hideGameCategoryTabs
        ? { ...value, game_category: GAME_CATEGORY.ALL }
        : value;
      setFilter(normalizedValue);
    },
    [hideGameCategoryTabs, setFilter],
  );

  /**
   * filter 引用稳定化：
   * useFilterCommon 每次 confirm 都返回新对象引用（即便 value 没变）；
   * 这里用 ref + 深比较只在内容真正变化时更新引用，避免下游 4 个模块的
   * `useMemo([filter])` / `useEffect([filter])` 因引用变化重跑。
   */
  const stableFilterRef = useRef(rawFilter);
  if (!isEqual(stableFilterRef.current, rawFilter)) {
    stableFilterRef.current = rawFilter;
  }
  const filter = stableFilterRef.current;

  // 品类切换自动更新时间
  const { onGenreChange } = useGenreDateAutoTime({
    byGameGenre,
    dayRange: 0,
    filterRef,
    filter,
  });

  const filterConfigProps = useMemo(() => {
    const base = !hideGameCategoryTabs
      ? ({} as Record<string, any>)
      : { ADCompareGame: { hideCategoryTabs: true } };
    return {
      ...base,
      ADCompareGame: {
        ...(base.ADCompareGame || {}),
        onGenreChange,
      },
    };
  }, [hideGameCategoryTabs, onGenreChange]);

  // 吸顶锚点联动
  const scrollContainerRef = useRef<HTMLElement>(null);
  const { activeAnchor, isSticky, scrollToAnchor } = useAnchorScroll({
    scrollRef: scrollContainerRef,
    anchors: ANCHORS,
    defaultAnchor: ANCHORS[0],
  });

  /** 从子组件标题常量构建锚点元信息（给 StickyNavigator 渲染 Segment） */
  const anchorMetas = useMemo<IAnchorMeta[]>(
    () => [
      { key: A_OVERVIEW, label: KPI_OVERVIEW_TITLE },
      { key: A_TREND, label: TREND_COMPARISON_TITLE },
      { key: A_CHANNEL, label: CHANNEL_COMPARISON_TITLE },
      { key: A_COUNTRY, label: COUNTRY_COMPARISON_TITLE },
    ],
    [],
  );

  const emptyTitleText = useMemo(() => {
    if (filter?.game_ids?.length === 0) {
      return 'Please select at least one game';
    }
    return 'No data available for the selected period';
  }, [filter]);

  const history = useHistory();
  const pageTitle =
    titleEn && titleCn ? getLanguage(isEn, titleEn, titleCn) : currentTitle;

  function gotoAdDeliveryOverview(gameid: string) {
    history.push(`/opinion/AdDeliveryOverview?gameid=${gameid}`, {
      start_time: filter.start_time,
      end_time: filter.end_time,
      date_type: filter.date_type,
    });
  }

  return (
    <FilterCondGameIdsProvider gameIds={condGameIds}>
      <Content
        className="dbom-competitor-ad-comparison-page"
        ref={scrollContainerRef}
      >
        {!hidePageHeader && <Content.Header title={pageTitle} />}
        <MarketContentBody
          loading={loading}
          horseRaceLampTips={adCreativesStopTipsMap}
          filterSlot={
            <FilterCommon
              {...filterProps}
              userDefaultValue={userDefaultFilter}
              loadingHeight={150}
              useOriDefaultValue
              realTimeMode
              filterConfigProps={filterConfigProps}
              confirm={handleConfirm}
              condGameIds={condGameIds}
            />
          }
        >
          <GameColorProvider gameIds={filter?.game_ids || []}>
            {/* 吸顶条 */}
            {isSticky && (
              <StickyNavigator
                anchors={anchorMetas}
                active={activeAnchor}
                onAnchorClick={scrollToAnchor}
                filter={filter}
                visible={isSticky}
              />
            )}
            <div className="dbom-competitor-ad-comparison-page__scroll">
              {/* 四大模块（组件自身接收 id + className，无需外层 div） */}
              <KpiOverview
                id={A_OVERVIEW}
                className="dbom-competitor-ad-comparison-page__module"
                filter={filter}
                emptyTitleText={emptyTitleText}
                gotoAdDeliveryOverview={gotoAdDeliveryOverview}
              />

              <TrendCompare
                id={A_TREND}
                className="dbom-competitor-ad-comparison-page__module"
                filter={filter}
                emptyTitleText={emptyTitleText}
              />

              <ChannelCompare
                id={A_CHANNEL}
                className="dbom-competitor-ad-comparison-page__module"
                filter={filter}
                emptyTitleText={emptyTitleText}
                gotoAdDeliveryOverview={gotoAdDeliveryOverview}
              />

              <CountryCompare
                id={A_COUNTRY}
                className="dbom-competitor-ad-comparison-page__module"
                filter={filter}
                emptyTitleText={emptyTitleText}
              />
            </div>
          </GameColorProvider>
        </MarketContentBody>
      </Content>
    </FilterCondGameIdsProvider>
  );
}

export default marketConnect<ICompetitorAdComparisonProps>({
  ids: [COMPETITOR_AD_LIST_API],
  dealwith: dealwithCompetitorAdComparison(COMPETITOR_AD_LIST_API, DEFAULT_GAME_CATEGORY),
})(CompetitorAdComparison);
